import AddButtonRounded from '@Components/AddButtonRounded';
import DeleteModal from '@Components/Modal/DeleteModal';
import theme from '@Components/Themes/Theme';
import { useDisclosure } from '@hooks/useDisclosure';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useNatureOfBusinessForm } from '../../NatureOfBusinessFormProvider';
import { BusinessGroupInfoFormValues } from '../../NatureOfBusinessFormProvider/types';
import FormBusinessGroupInformation from './FormBusinessGroupInformation';

const BusinessGroupInformation = () => {
  const { isCompletedTh, isCompletedEn } =
    useWatch({
      name: 'productInformation.completedSection',
    }) ?? ({} as CompleteSectionFormValues);
  const [opened, { open, close }] = useDisclosure();
  const [indexSelected, setIndexSelected] = useState<number>(0);
  const { reset, getValues } = useNatureOfBusinessForm();

  const { fields, append, remove } = useFieldArray({
    name: 'productInformation.businessGroupInfos',
  });
  const [expanded, setExpanded] = useState<string[]>([]);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  if (fields.length === 0) {
    append({} as BusinessGroupInfoFormValues);
  }

  const handleAddBusinessGroup = () => {
    append({} as BusinessGroupInfoFormValues);
    const newIndex = fields.length;
    setExpanded((prev) => [...prev, `panel${newIndex}`]);
  };

  const handleOpenDisclosure = (businessGroupIndex: number) => {
    setIndexSelected(businessGroupIndex);
    open();
  };

  const handleSetExpanded =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setExpanded((prev) => [...prev, panel]);
      } else {
        setExpanded((prev) => prev.filter((item) => item !== panel));
      }
    };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setExpanded((prev) => [...prev, 'panel0']);
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentSectionRef = sectionRef.current;
    if (currentSectionRef) {
      observer.observe(currentSectionRef);
    }

    return () => {
      if (currentSectionRef) {
        observer.unobserve(currentSectionRef);
      }
    };
  }, []);

  return (
    <Box ref={sectionRef}>
      {fields.map((item, index) => (
        <Accordion
          key={item.id}
          expanded={expanded.includes(`panel${index}`)}
          onChange={handleSetExpanded(`panel${index}`)}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '4px',
            marginTop: '12px',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${item.id || index}-content`}
            id={`${item.id || index}-header`}
          >
            <Typography
              fontWeight={600}
              sx={{ flexGrow: 1 }}
              alignContent={'center'}
            >
              {`สายผลิตภัณฑ์ บริการ หรือกลุ่มธุรกิจที่ ${index + 1}`}
            </Typography>
            {fields.length > 1 && (
              <IconButton
                size="small"
                edge="end"
                aria-label="delete"
                onClick={() => handleOpenDisclosure(index)}
                sx={{ mx: 1 }}
                disabled={isCompletedTh || isCompletedEn}
              >
                <DeleteOutlined sx={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <FormBusinessGroupInformation index={index} />
          </AccordionDetails>
        </Accordion>
      ))}

      <DeleteModal
        topic={`สายผลิตภัณฑ์ บริการ หรือกลุ่มธุรกิจที่ ${indexSelected + 1}`}
        opened={opened}
        close={close}
        confirm={() => {
          remove(indexSelected);
          const updatedValues = getValues();
          reset({
            ...updatedValues,
            productInformation: {
              ...updatedValues.productInformation,
              businessGroupInfos:
                updatedValues.productInformation?.businessGroupInfos,
            },
          });
          close();
        }}
      />
      <Box mt={2}>
        <AddButtonRounded
          name="เพิ่มสายผลิตภัณฑ์หรือกลุ่มธุรกิจ"
          onClick={handleAddBusinessGroup}
          disabled={isCompletedTh || isCompletedEn}
        />
      </Box>
    </Box>
  );
};

export default BusinessGroupInformation;
